import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import Text from "@galaxy-io/dls/text/Text";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { DrawerRow } from "@/components/workspace/drawer-content";
import { MultiSelectField } from "@/components/workspace/select-field";
import { apiClient, unwrap } from "@/lib/api-client";

export function useTags() {
  return useQuery({
    queryKey: ["tags"],
    queryFn: ({ signal }) => unwrap(apiClient().GET("/tags", { signal })),
  });
}
export function TagFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const tags = useTags();
  return (
    <>
      <MultiSelectField
        label="Tags (match any)"
        placeholder={tags.isPending ? "Loading tags…" : "All tags"}
        value={value}
        onChange={onChange}
        disabled={!tags.data}
        options={(tags.data ?? []).map((tag) => ({ value: tag.id, label: tag.name }))}
      />
      {tags.error && (
        <Button
          size={ButtonSize.SMALL}
          variant={ButtonVariant.SECONDARY}
          label="Retry tags"
          onClick={() => void tags.refetch()}
        />
      )}
      {!!value && (
        <Button
          size={ButtonSize.SMALL}
          variant={ButtonVariant.TERTIARY}
          label="Clear tags"
          onClick={() => onChange("")}
        />
      )}
    </>
  );
}

export function EntityTags({
  asValue = false,
  kind,
  id,
}: {
  kind: "person" | "company";
  id: string;
  asValue?: boolean;
}) {
  const tags = useTags();
  const client = useQueryClient();
  const path = kind === "person" ? "/people/{id}/tags" : "/companies/{id}/tags";
  const selected = useQuery({
    queryKey: ["entity-tags", kind, id],
    queryFn: ({ signal }) => unwrap(apiClient().GET(path, { params: { path: { id } }, signal })),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save(value: string) {
    // Display all effective tags, but keep rule-only selections out of manual writes.
    const ruleOnlyIds = new Set(
      (selected.data ?? []).filter((tag) => tag.manual === false).map((tag) => tag.id),
    );
    const tagIds = value ? value.split(",").filter((tagId) => !ruleOnlyIds.has(tagId)) : [];
    setBusy(true);
    setError(null);
    try {
      const saved = await unwrap(
        apiClient().PUT(path, {
          params: { path: { id } },
          body: { tagIds },
        }),
      );
      client.setQueryData(["entity-tags", kind, id], saved);
      void client.invalidateQueries();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const content = (
    <>
      {tags.error || selected.error ? (
        <QueryState
          error={tags.error || selected.error}
          onRetry={() => {
            void tags.refetch();
            void selected.refetch();
          }}
        />
      ) : !tags.data || !selected.data ? (
        <Text>Loading tags…</Text>
      ) : tags.data.length === 0 ? (
        <Link href="/tags">Manage tags in Configuration</Link>
      ) : (
        <MultiSelectField
          showLabel={false}
          label="Tags"
          placeholder="Add tags…"
          width={188}
          disabled={busy}
          value={selected.data.map((tag) => tag.id).join(",")}
          onChange={(value) => void save(value)}
          options={tags.data.map((tag) => ({ value: tag.id, label: tag.name }))}
        />
      )}
      {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
    </>
  );
  return asValue ? content : <DrawerRow label="Tags">{content}</DrawerRow>;
}
